'use client';
import { createClient } from '@/lib/supabase/client';
import { getCurrentStoreId } from './storeContext';
import { setStoreContext } from '@/lib/supabase/withStoreContext';

export interface AuditLog {
  id: string;
  userId: string | null;
  userName: string;
  actorRole: string;
  actorEmail: string;
  action: string;
  description: string;
  ip: string;
  createdAt: string;
}

function toLog(row: any): AuditLog {
  return {
    id: row.id,
    userId: row.user_id ?? null,
    userName: row.user_name ?? 'Système',
    actorRole: row.actor_role ?? '',
    actorEmail: row.actor_email ?? '',
    action: row.action ?? '',
    description: row.description ?? '',
    ip: row.ip ?? '',
    createdAt: row.created_at,
  };
}

async function getCurrentActorInfo(): Promise<{ userId: string | null; userName: string; actorRole: string; actorEmail: string }> {
  try {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { userId: null, userName: 'Système', actorRole: '', actorEmail: '' };

    // Try to get role from ef_app_users
    const { data: appUser } = await supabase
      .from('ef_app_users')
      .select('full_name, role, email')
      .eq('auth_user_id', user.id)
      .maybeSingle();

    const userName = appUser?.full_name || user.email?.split('@')[0] || 'Utilisateur';
    const actorRole = appUser?.role || user.user_metadata?.role || '';
    const actorEmail = appUser?.email || user.email || '';

    return { userId: user.id, userName, actorRole, actorEmail };
  } catch {
    return { userId: null, userName: 'Système', actorRole: '', actorEmail: '' };
  }
}

export const auditService = {
  async getAll(limit = 500): Promise<AuditLog[]> {
    const supabase = createClient();
    const storeId = await getCurrentStoreId();
    await setStoreContext(supabase, storeId);
    let q = supabase
      .from('ef_audit_logs')
      .select('id, user_id, user_name, actor_role, actor_email, action, description, ip, created_at')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (storeId) q = q.eq('store_id', storeId);
    const { data, error } = await q;
    if (error) { console.error('auditService.getAll:', error.message); return []; }
    return (data || []).map(toLog);
  },

  async log(action: string, description: string, overrideName?: string, ip = '', customDate?: string): Promise<void> {
    try {
      const supabase = createClient();
      const storeId = await getCurrentStoreId();
      await setStoreContext(supabase, storeId);
      const actor = await getCurrentActorInfo();
      const payload: any = {
        user_id: actor.userId,
        user_name: overrideName || actor.userName,
        actor_role: actor.actorRole,
        actor_email: actor.actorEmail,
        action,
        description,
        ip,
        store_id: storeId,
      };
      // If a custom date is provided (e.g. backdated purchase), override created_at
      if (customDate) {
        payload.created_at = `${customDate}T12:00:00.000Z`;
      }
      const { error } = await supabase
        .from('ef_audit_logs')
        .insert(payload);
      if (error) console.error('auditService.log:', error.message);
    } catch (e) {
      console.error('auditService.log exception:', e);
    }
  },
};
